import {groupByUniqueKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import produce from 'immer';
import React, {useMemo} from 'react';

import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import MulticolorStripedCircle from 'samples/plate-maps/MulticolorStripedCircle';
import {
  AssignmentGroup,
  AssignmentGroupValueTypeByLayer,
  BaseSchematizedAttribute,
  PlateMap,
  PlateMapWellDisplayLayer,
} from 'samples/plate-maps/PlateMapConstants';
import {
  applySchemaTagEditToSelected,
  fetchDropdownOptionsForSelector,
  getSchematizedAttributeTagFromWell,
  SchemaSelectorOption,
  writeSchemaTagToDraftWell,
} from 'samples/plate-maps/PlateMapUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_POWDER_LIGHT, GRAY_DARKER} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

export interface SelectorFieldAssignmentSettingsProps {
  plateMap: PlateMap;
  onEditPlateMap: (plateMap: PlateMap) => void;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
  selectedAttribute: BaseSchematizedAttribute<SchemaField>;
  assignmentGroupsById: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR]
    >;
  };
}

/**
 * Settings to assign selector schema field values to a plate cells.
 */
export default function SelectorFieldAssignmentSettings({
  plateMap,
  onEditPlateMap,
  selectedWellIndices,
  deselectWells,
  selectedAttribute,
  assignmentGroupsById,
}: SelectorFieldAssignmentSettingsProps): JSX.Element {
  const [selectorOptions, setSelectorOptions] = useStateIfMounted<Array<SchemaSelectorOption>>([]);
  const optionsById = useMemo(
    () => groupByUniqueKey<SchemaSelectorOption, string>(selectorOptions, (option) => option.id),
    [selectorOptions]
  );
  useOnMount(() => {
    async function initializeOptions(): Promise<void> {
      const options = await fetchDropdownOptionsForSelector(
        selectedAttribute.schemaField.schemaFieldSelectorId!
      );
      setSelectorOptions(options.filter(({archiveRecord}) => archiveRecord == null));
    }
    initializeOptions();
  });

  // Assign the given dropdown optionID to all selected wells on the plate map
  // For multi-select fields, add the selected option to any currently selected option.
  // For single-select fields, replace the current selection.
  function assignValueToSelected(option: SchemaSelectorOption): void {
    let updatedPlateMap: PlateMap;

    if (selectedAttribute.schemaField.isMulti) {
      updatedPlateMap = produce(plateMap, (draftPlateMap) => {
        [...selectedWellIndices].forEach((index) => {
          const currentSchemaTag = getSchematizedAttributeTagFromWell(
            draftPlateMap.wells[index],
            selectedAttribute
          );
          const currentSelectorIds = currentSchemaTag?.schemaFieldSelectorOptionIds ?? [];
          const newUniqueSelectorIds = new Set([...currentSelectorIds, option.id]);
          const schemaFieldSelectorOptionIds = [...newUniqueSelectorIds];
          const selectorFieldOptionNames = schemaFieldSelectorOptionIds.map((id) => optionsById[id].name);
          const value = selectorFieldOptionNames.join(', ');
          writeSchemaTagToDraftWell(draftPlateMap.wells[index], selectedAttribute, {
            value,
            schemaFieldSelectorOptionIds,
          });
        });
      });
    } else {
      updatedPlateMap = applySchemaTagEditToSelected(
        plateMap,
        selectedAttribute,
        {value: option.name, schemaFieldSelectorOptionIds: [option.id]},
        selectedWellIndices
      );
    }
    onEditPlateMap(updatedPlateMap);
    deselectWells();
  }

  return (
    <div {...getTestProps({component: 'SelectorFieldAssignmentSettings', element: 'root'})}>
      <p className={css(styles.helpText)}>
        Select wells in the plate, then select {selectedAttribute.schemaField.isMulti ? 'labels' : 'a label'}{' '}
        to apply to them
      </p>
      <div className={classnames(css(styles.optionList), 'scrollbar-override')}>
        {selectorOptions.map((option) => (
          <div
            key={option.id}
            className={css(styles.selectorOption)}
            {...getActionButtonRoleProps({
              onPress: () => {
                if (selectedWellIndices.size > 0) {
                  assignValueToSelected(option);
                }
              },
            })}
            {...getTestProps({
              component: 'SelectorFieldAssignmentSettings',
              element: 'option',
              key: option.id,
            })}
          >
            <MulticolorStripedCircle
              cx={6}
              cy={6}
              radius={6}
              colors={[assignmentGroupsById[option.id]?.color]}
            />
            <span className={css(styles.optionName)}>{option.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
  optionList: {
    marginTop: '6px',
    overflowY: 'auto',
  },
  selectorOption: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 4px',
    width: '100%',
    ':hover': {
      backgroundColor: BLUE_POWDER_LIGHT,
    },
  },
  optionName: {
    marginLeft: '4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});
